/**
 * Image generation copy (spec 027). Limits are protection, never an
 * allowance: nothing here says "free", "included" or "not charged", and
 * sprite states are "expressions".
 */
import type { DraftFailReason, ImageSettingsView } from '../../../../Shared/src/images';
import { DEFAULT_DRAFT_COUNT, IMAGE_UNITS, jobUnitsNeeded } from '../../../../Shared/src/images';

export const IMAGE_TEXT = {
  generate: 'Generate',
  /** A filled slot's secondary Generate (D-21): the same flow with `replace: true`. */
  generateNew: 'Generate new',
  offNote: 'Image generation is off for this workspace. An admin can connect an image key in Settings › Integrations.',
  notSetUp: 'Image generation is not set up on this server.',
  interrupted: 'Interrupted. Generate again.',
  drawing: (n: number) => `Drawing ${n} low-res drafts from the brief…`,
  drawingNote: 'Drafts are 256 px. The one you accept is redrawn at full size before it goes into the library.',
  ready: (ready: number, total: number) => `${ready} of ${total} ready`,
  draftFailed: (n: number) => `Draft ${n} failed`,
  failedCounts: 'The failed request still counts toward the monthly limit.',
  more: (n: number) => `${n} more`,
  regenerate: 'Regenerate with new brief',
  libraryName: 'Library name',
  placeIn: 'Place in',
  accept: 'Accept and place',
  drawingFull: 'Drawing full size…',
  fullFailed: 'Full size failed',
  retry: 'Retry',
  cancel: 'Cancel',
  undo: 'Undo',
  placeFailed: "The picture is in the library but couldn't be placed. Try Choose from library.",
} as const;

/** One line per failure reason worth telling (others show only "Draft N failed"). */
export const REASON_TEXT: Partial<Record<DraftFailReason, string>> = {
  rejected_key: 'Your OpenAI key was rejected. An admin can replace it in Settings › Integrations.',
  rate_limited: 'The image service is busy. Retry in a moment.',
  refused: 'The image service refused this brief. Change the brief and try again.',
};

export const limitReachedText = (monthly: number): string =>
  `This workspace has reached its monthly image limit (${monthly}). It resets on the 1st; an admin can see it in Settings › Integrations.`;

export const needsText = (needed: number, left: number): string =>
  `Needs ${needed} of this month's limit, only ${left} left.`;

export const usedText = (s: Pick<ImageSettingsView, 'limit'>): string => `Monthly limit: ${s.limit.used} of ${s.limit.monthly} used`;

/** The Generate tooltip's first line: what will run before anything does. */
export const generateLine = (s: Pick<ImageSettingsView, 'mode'>, count = DEFAULT_DRAFT_COUNT): string =>
  `GPT Image · ${count} drafts at 256 px · about 10 seconds${s.mode === 'byok' ? ' · your OpenAI key' : ''}`;

/** The tooltip lines of a Generate for `slots` slots; `disabled` when fewer units are left than it needs. */
export function generateTooltip(s: ImageSettingsView, slots = 1, count = DEFAULT_DRAFT_COUNT): { lines: string[]; disabled: boolean } {
  const needed = jobUnitsNeeded(slots, count);
  if (s.limit.left < needed) return { lines: [needs(needed, s.limit.left)], disabled: true };
  return { lines: [generateLine(s, count), usedText(s)], disabled: false };
}
const needs = needsText;

/** The status line above the Look row. */
export function statusLineText(s: ImageSettingsView | undefined): string {
  if (!s || s.mode === 'off' || !s.available) return 'Image generation off for this workspace';
  const used = `${s.limit.used} of ${s.limit.monthly} used this month`;
  return s.mode === 'byok' ? `Image generation on · your OpenAI key · ${used}` : `Image generation on · ${used}`;
}

/** "Characters › Jordan", "Props", "Backgrounds", "Pictures" (D-38). */
export const libraryPathOf = (kind: 'character' | 'prop' | 'background' | 'other', subject: string): string =>
  kind === 'character' ? `Characters › ${subject}` : kind === 'prop' ? 'Props' : kind === 'background' ? 'Backgrounds' : 'Pictures';

/** A character's places are its lines; any other kind's are spots, as 026's placement line says. */
export const placedText = (path: string, places: number, kind: 'character' | 'prop' | 'background' | 'other' = 'character'): string => {
  const noun = kind === 'character' ? (places === 1 ? 'line' : 'lines') : (places === 1 ? 'spot' : 'spots');
  return `Added to Library › ${path} and placed in ${places} ${noun}.`;
};

/** The panel's brief and framing copy (D-22, D-23). */
export const BRIEF_TEXT = {
  label: 'Brief',
  write: 'Write from the story',
  writing: 'Writing a brief from the story…',
  writeLine: '1 brief · 1 of this month\'s limit',
  writeFailed: "The brief couldn't be written. Try again or type it yourself.",
  saveFailed: "The brief couldn't be saved. Try again.",
  needsBrief: 'Write a brief first: type it or use Write from the story.',
  framing: 'Framing',
  framingNote: (subject: string) => `Every expression of ${subject} uses this framing, from the next drafts.`,
  /** D-33: on the master once the character has a picture. */
  framingResets: (subject: string) => ` Changing it resets ${subject}.`,
  /** D-33: an expression's details; framing is chosen on the master only. */
  framingOnMaster: (framing: string) => `Framing: ${framing}.`,
  framingMasterLink: (label: string) => `Change framing on the master picture, ${label}`,
  framingConfirmTitle: (subject: string) => `Change the framing and reset ${subject}?`,
  framingConfirmText: (subject: string, framing: string) =>
    `Changing the framing to ${framing} resets ${subject}: the master picture and every expression go back to placeholders, drafts still running stop, and each picture has to be generated again. The briefs are kept.`,
  framingConfirmKeeps: "The story's look stays. The pictures stay in your library, and Undo puts everything back.",
  framingConfirm: 'Change and reset',
  framingFailed: "The framing couldn't be changed. Try again.",
} as const;

/** The panel's brief and framing copy for an owner (spec 030 D-13): "Write from the scene" for a scene. */
export function briefTextFor(noun: 'story' | 'scene'): typeof BRIEF_TEXT {
  if (noun === 'story') return BRIEF_TEXT;
  const swap = (t: string) => t.replace(/\bstory\b/g, 'scene').replace(/\bStory\b/g, 'Scene');
  return Object.fromEntries(Object.entries(BRIEF_TEXT).map(([k, v]) => [k, typeof v === 'string' ? swap(v) : v])) as unknown as typeof BRIEF_TEXT;
}

/** The tooltip of "Write from the story": the cost first; disabled with no unit left. */
export function writeBriefTooltip(s: ImageSettingsView): { lines: string[]; disabled: boolean } {
  if (s.limit.left < IMAGE_UNITS.briefText) return { lines: [needsText(IMAGE_UNITS.briefText, s.limit.left)], disabled: true };
  return { lines: [BRIEF_TEXT.writeLine, usedText(s)], disabled: false };
}

/** "2 pictures without a brief are skipped." (Draft all, D-23). */
export const skippedText = (n: number): string =>
  `${n} ${n === 1 ? 'picture' : 'pictures'} without a brief ${n === 1 ? 'is' : 'are'} skipped.`;
